import { STATUS_PRECEDENCE, worstStatus } from './statusLabels';
import type {
  JurisdictionAvailability,
  JurisdictionCatalog,
  JurisdictionEntry,
  JurisdictionSupport,
  JurisdictionType,
  OverallStatus,
  RequirementStatus,
  ResponsibleParty,
  TaxEffect,
  TaxSetupCounts,
  TaxSetupRequirement,
  TaxSetupStatus,
} from './types';

/**
 * Wire mappers for the tax jurisdiction catalog and the tax setup status. They are TOLERANT: a member the server adds later is ignored, a member that
 * is missing or has a value this build does not know falls back to the cautious reading (never to "Complete", never to "Certified").
 */

type Raw = Record<string, unknown>;
const isRecord = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const bool = (v: unknown, fallback = false): boolean => (typeof v === 'boolean' ? v : fallback);
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | null => (typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : null);

const TYPES: readonly JurisdictionType[] = ['State', 'District', 'Territory'];
const AVAILABILITIES: readonly JurisdictionAvailability[] = ['Certified', 'PilotOnly', 'ConfigurationAvailable', 'CalculationNotYetCertified', 'FilingNotYetAvailable', 'Unsupported'];
const REQUIREMENT_STATUSES: readonly RequirementStatus[] = ['Complete', 'NeedsInformation', 'NeedsReview', 'NotApplicable', 'Unsupported', 'Expired', 'EffectiveInFuture'];
const PARTIES: readonly ResponsibleParty[] = ['Employee', 'Employer', 'PayrollAdmin', 'TaxReviewer', 'EPN'];
const EFFECTS: readonly TaxEffect[] = ['Calculation', 'Payment', 'Filing'];

const GROUP_LABEL: Record<JurisdictionType, string> = { State: 'States', District: 'District', Territory: 'U.S. Territories and Commonwealths' };

function mapSupport(raw: unknown): JurisdictionSupport {
  const r = isRecord(raw) ? raw : {};
  return {
    configuration: str(r.configuration, 'Unknown'),
    calculation: str(r.calculation, 'Unknown'),
    withholding: str(r.withholding, 'Unknown'),
    unemployment: str(r.unemployment, 'Unknown'),
    filing: str(r.filing, 'Unknown'),
    payment: str(r.payment, 'Unknown'),
  };
}

/** One catalog entry, or null when it has no usable code or name. An unknown `availability` becomes `Unsupported` (never an over-claim). */
export function mapJurisdictionEntry(raw: unknown): JurisdictionEntry | null {
  if (!isRecord(raw)) return null;
  const code = str(raw.code).trim().toUpperCase();
  const name = str(raw.name).trim();
  if (!code || !name) return null;
  const type = oneOf(raw.type, TYPES) ?? 'State';
  return {
    code,
    name,
    type,
    isActive: bool(raw.isActive, true),
    groupLabel: str(raw.groupLabel) || GROUP_LABEL[type],
    availability: oneOf(raw.availability, AVAILABILITIES) ?? 'Unsupported',
    support: mapSupport(raw.support),
    noWageIncomeTax: typeof raw.noWageIncomeTax === 'boolean' ? raw.noWageIncomeTax : null,
    approvalStatus: str(raw.approvalStatus, 'Unknown'),
    effectiveFrom: strOrNull(raw.effectiveFrom),
    effectiveTo: strOrNull(raw.effectiveTo),
    officialSources: list(raw.officialSources).filter((s): s is string => typeof s === 'string'),
    lastVerifiedOn: strOrNull(raw.lastVerifiedOn),
    approver: strOrNull(raw.approver),
  };
}

/** The catalog. Inactive entries are kept in `items` (callers that offer a choice filter them); the same code is never listed twice. */
export function mapJurisdictionCatalog(raw: unknown): JurisdictionCatalog {
  const r = isRecord(raw) ? raw : {};
  const seen = new Set<string>();
  const items: JurisdictionEntry[] = [];
  for (const item of list(r.items)) {
    const mapped = mapJurisdictionEntry(item);
    if (!mapped || seen.has(mapped.code)) continue;
    seen.add(mapped.code);
    items.push(mapped);
  }
  return { version: str(r.version), status: str(r.status, 'Unknown'), note: str(r.note), items };
}

function mapRequirement(raw: unknown): TaxSetupRequirement | null {
  if (!isRecord(raw)) return null;
  const key = str(raw.key);
  if (!key) return null;
  return {
    key,
    section: str(raw.section),
    title: str(raw.title) || key,
    // An unknown status is never shown as Complete: it reads as "Needs review".
    status: oneOf(raw.status, REQUIREMENT_STATUSES) ?? 'NeedsReview',
    responsibleParty: oneOf(raw.responsibleParty, PARTIES) ?? 'EPN',
    blocksSetupCompletion: bool(raw.blocksSetupCompletion),
    affects: list(raw.affects).flatMap((e) => {
      const effect = oneOf(e, EFFECTS);
      return effect ? [effect] : [];
    }),
    message: str(raw.message),
    nextAction: str(raw.nextAction),
  };
}

export function countRequirements(requirements: readonly Pick<TaxSetupRequirement, 'status'>[]): TaxSetupCounts {
  const n = (s: RequirementStatus) => requirements.filter((r) => r.status === s).length;
  const needsInformation = n('NeedsInformation');
  const needsReview = n('NeedsReview');
  const unsupported = n('Unsupported');
  const expired = n('Expired');
  return {
    complete: n('Complete'),
    needsInformation,
    needsReview,
    notApplicable: n('NotApplicable'),
    unsupported,
    expired,
    effectiveInFuture: n('EffectiveInFuture'),
    needsAttention: needsInformation + needsReview + unsupported + expired,
  };
}

function mapCounts(raw: unknown, requirements: TaxSetupRequirement[]): TaxSetupCounts {
  const computed = countRequirements(requirements);
  if (!isRecord(raw)) return computed;
  const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback);
  const needsInformation = num(raw.needsInformation, computed.needsInformation);
  const needsReview = num(raw.needsReview, computed.needsReview);
  const unsupported = num(raw.unsupported, computed.unsupported);
  const expired = num(raw.expired, computed.expired);
  return {
    complete: num(raw.complete, computed.complete),
    needsInformation,
    needsReview,
    notApplicable: num(raw.notApplicable, computed.notApplicable),
    unsupported,
    expired,
    effectiveInFuture: num(raw.effectiveInFuture, computed.effectiveInFuture),
    needsAttention: num(raw.needsAttention, needsInformation + needsReview + unsupported + expired),
  };
}

/**
 * A tax setup status, or null when the value is not an object. The overall status is never trusted past the requirements: a server "Complete" next to
 * a requirement that is not complete is shown as the worst applicable requirement instead, so nothing reads Complete while something is open.
 */
export function mapTaxSetupStatus(raw: unknown): TaxSetupStatus | null {
  if (!isRecord(raw)) return null;
  const requirements = list(raw.requirements).flatMap((r) => {
    const mapped = mapRequirement(r);
    return mapped ? [mapped] : [];
  });
  const declared = oneOf<OverallStatus>(raw.status, STATUS_PRECEDENCE) ?? 'NeedsReview';
  const worst = worstStatus(requirements);
  const status: OverallStatus = declared === 'Complete' && worst !== 'Complete' && worst !== 'NotApplicable' ? (worst as OverallStatus) : declared;
  return {
    evaluatedAtUtc: str(raw.evaluatedAtUtc),
    referenceDataVersion: str(raw.referenceDataVersion),
    referenceDataStatus: str(raw.referenceDataStatus, 'Unknown'),
    status,
    counts: mapCounts(raw.counts, requirements),
    requirements,
    warnings: list(raw.warnings).filter((w): w is string => typeof w === 'string'),
  };
}
